import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Palette, Loader2, Save, Globe, Image as ImageIcon, Check, Radio, Facebook, Clapperboard } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Branding() {
  const { checkAuth } = useAuth();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/branding").then((r) => setB(r.data)).catch(() => toast.error("Failed to load branding")).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/branding", { logo_url: b.logo_url, accent_color: b.accent_color, subdomain: b.subdomain, facebook_rtmp_url: b.facebook_rtmp_url, facebook_stream_key: b.facebook_stream_key, default_stream_title: b.default_stream_title, default_stream_description: b.default_stream_description });
      setB(res.data);
      toast.success("Branding saved");
      checkAuth?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save branding");
    } finally { setSaving(false); }
  };

  if (loading || !b) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;

  const fullDomain = b.subdomain ? `${b.subdomain}.${b.base_domain}` : "";

  return (
    <div className="p-6 lg:p-8 max-w-[900px] mx-auto">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight">Organization Branding</h1>
        <p className="text-gray-400 mt-1.5 text-sm">Your logo, accent color and subdomain appear on your players and CTV feeds.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
            <p className="text-[10px] font-semibold text-gray-500 tracking-widest flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5 text-amber-400" /> LOGO</p>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Logo Image URL</Label>
              <Input data-testid="branding-logo-url" value={b.logo_url} onChange={(e) => setB({ ...b, logo_url: e.target.value })} placeholder="https://…/logo.png" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
              <p className="text-[11px] text-gray-500">Paste a hosted image URL (PNG with transparency looks best).</p>
            </div>
          </div>

          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
            <p className="text-[10px] font-semibold text-gray-500 tracking-widest flex items-center gap-1.5"><Palette className="h-3.5 w-3.5 text-amber-400" /> ACCENT COLOR</p>
            <div className="flex items-center gap-3">
              <input data-testid="branding-accent-color" type="color" value={b.accent_color} onChange={(e) => setB({ ...b, accent_color: e.target.value })} className="h-11 w-16 rounded-lg bg-transparent border border-gray-800 cursor-pointer" />
              <Input data-testid="branding-accent-hex" value={b.accent_color} onChange={(e) => setB({ ...b, accent_color: e.target.value })} className="w-32 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 font-mono" />
            </div>
          </div>

          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
            <p className="text-[10px] font-semibold text-gray-500 tracking-widest flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-amber-400" /> SUBDOMAIN</p>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Reserve your subdomain</Label>
              <div className="flex items-center gap-0 rounded-lg border border-gray-800 bg-gray-950 overflow-hidden focus-within:ring-2 focus-within:ring-amber-500/40">
                <Input data-testid="branding-subdomain" value={b.subdomain} onChange={(e) => setB({ ...b, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="grace" className="border-0 bg-transparent focus-visible:ring-0 rounded-none flex-1" />
                <span className="px-3 text-sm text-gray-500 shrink-0 select-none">.{b.base_domain}</span>
              </div>
              <p className="text-[11px] text-gray-500">3-32 chars, lowercase letters, numbers and hyphens. Used on your embed and feed links.</p>
            </div>
          </div>

          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
            <div>
              <p className="text-[10px] font-semibold text-gray-500 tracking-widest flex items-center gap-1.5"><Clapperboard className="h-3.5 w-3.5 text-amber-400" /> STREAM DEFAULTS</p>
              <p className="text-[11px] text-gray-500 mt-1.5">When a stream goes live from the encoder, we automatically apply this title and description — no prompt needed on the encoder side.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Default Stream Title</Label>
              <Input data-testid="branding-default-stream-title" value={b.default_stream_title || ""} onChange={(e) => setB({ ...b, default_stream_title: e.target.value })} placeholder="Sunday Service" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Default Stream Description</Label>
              <Textarea data-testid="branding-default-stream-description" value={b.default_stream_description || ""} onChange={(e) => setB({ ...b, default_stream_description: e.target.value })} placeholder="Live worship service" rows={3} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
            </div>
          </div>

          <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5">
            <div>
              <p className="text-[10px] font-semibold text-gray-500 tracking-widest flex items-center gap-1.5"><Facebook className="h-3.5 w-3.5 text-amber-400" /> FACEBOOK LIVE FORWARDING</p>
              <p className="text-[11px] text-gray-500 mt-1.5">When a stream goes live, we'll automatically forward it to Facebook using the RTMP URL and stream key below. Leave the key blank to disable.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Facebook RTMP URL</Label>
              <Input data-testid="branding-facebook-rtmp-url" value={b.facebook_rtmp_url || ""} onChange={(e) => setB({ ...b, facebook_rtmp_url: e.target.value })} placeholder="rtmps://live-api-s.facebook.com:443/rtmp/" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 font-mono text-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Facebook Stream Key</Label>
              <Input data-testid="branding-facebook-stream-key" type="password" value={b.facebook_stream_key || ""} onChange={(e) => setB({ ...b, facebook_stream_key: e.target.value })} placeholder="Paste your Facebook Live stream key" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 font-mono text-sm" />
              <p className="text-[11px] text-gray-500">Copy the <span className="text-gray-300">Stream key</span> from Facebook Live Producer (Go live → Streaming software).</p>
            </div>
          </div>

          <button data-testid="save-branding" onClick={save} disabled={saving} className="gold-gradient text-black font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-60 cursor-pointer">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Branding
          </button>
        </div>

        {/* Live preview */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 space-y-4">
            <p className="text-[10px] font-semibold text-gray-500 tracking-widest">PREVIEW</p>
            <motion.div layout className="rounded-2xl border border-gray-800 overflow-hidden bg-black">
              <div className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold" style={{ background: b.accent_color, color: "#0B0F17" }}>
                {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-5 object-contain" /> : <Radio className="h-4 w-4" />}
                {b.organization || "Your Church"}
              </div>
              <div className="aspect-video bg-grid flex items-center justify-center">
                <span className="h-14 w-14 rounded-full flex items-center justify-center" style={{ background: b.accent_color, color: "#0B0F17" }}>
                  <Radio className="h-6 w-6" />
                </span>
              </div>
            </motion.div>
            <div className="rounded-xl border border-gray-800 bg-card p-4">
              <p className="text-xs text-gray-500 mb-1">Your branded link</p>
              {fullDomain ? (
                <code data-testid="branding-domain-preview" className="text-sm text-amber-300 font-mono break-all flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-400" /> {fullDomain}</code>
              ) : (
                <p className="text-sm text-gray-600">Reserve a subdomain to get a branded link.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
